import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

import { SnLprHotListGroup } from '../models';

@Injectable({
  providedIn: 'root',
})
export class LprHotlistGroupsAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getGroups(
    id?: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnLprHotListGroup>> {
    return this._httpClient.get(`/api/lpr/hotlists/${id}/groups/`, { params }).pipe(
      tap({
        next: () => {},
        error: () => {
          this._toastrService.error(t('vehicleIntelligence.hotlist.groups.unableToRetrieve'));
        },
      }),
      map((response: any) => ({
        count: response?.length,
        results: [...response],
      })),
    );
  }

  public getGroup(id: string): Observable<SnLprHotListGroup> {
    return this._httpClient
      .get(`/api/lpr/hotlist/groups/${id}/`)
      .pipe(map((response: any) => new SnLprHotListGroup(response)));
  }

  public create(data: any): Observable<SnLprHotListGroup> {
    if (data.id) {
      return this.update(data.id, data);
    }
    return this._httpClient.post('/api/lpr/hotlist/groups/', data).pipe(
      tap({
        next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.groups.saved')),
        error: () => this._toastrService.error(t('vehicleIntelligence.hotlist.groups.unableToSave')),
      }),
      map((response: any) => new SnLprHotListGroup(response)),
    );
  }

  public update(id: string, data: any): Observable<SnLprHotListGroup> {
    return this._httpClient.patch(`/api/lpr/hotlist/groups/${id}/`, data).pipe(
      tap({
        next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.groups.saved')),
        error: () => this._toastrService.error(t('vehicleIntelligence.hotlist.groups.unableToSave')),
      }),
      map((response: any) => new SnLprHotListGroup(response)),
    );
  }

  public delete(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('vehicleIntelligence.hotlist.groups.deleteConfirmation'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/lpr/hotlist/groups/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.groups.deleted')),
          error: () =>
            this._toastrService.error(t('vehicleIntelligence.hotlist.groups.couldNotDelete')),
        }),
      );
  }
}
